// The drone page's Repair key: opens a right-hand drawer with the repair pull
// for this drone and the repair pulls it already has.

import React, { useCallback, useEffect, useState } from 'react';
import { buttonClass } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import FadeIn from '../../components/ui/FadeIn';
import Reveal from '../../components/ui/Reveal';
import { usePresence } from '../../components/ui/motion';
import { EmptyState } from '../../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../../components/ui/Skeleton';
import { Tooltip } from '../../components/ui/Tooltip';
import { itemLabel } from '../../components/ItemName';
import { formatTimestamp } from '../../utils/date-utils';
import PullForm from './PullForm';
import PutBackPopover from './PutBackPopover';
import SideDrawer from './SideDrawer';
import { usePullsApi, type PullLine } from './pullsApi';

interface Props {
  serialnumber: string;
  className?: string;
}

const DroneRepair: React.FC<Props> = ({ serialnumber, className = '' }) => {
  const api = usePullsApi();
  const [open, setOpen] = useState(false);
  const { mounted } = usePresence(open);
  const [lines, setLines] = useState<PullLine[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoadError(null);
    api
      .droneLines(serialnumber)
      .then((r) => setLines(r.lines || []))
      .catch((err: any) => {
        setLines([]);
        setLoadError(err?.message || 'The repair history did not load. Close this and open it again.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialnumber]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <>
      <span className={`inline-flex ${className}`}>
        <Tooltip label="Pull a part from stock into this drone's repair">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={buttonClass('tonal')}
          >
            Repair
          </button>
        </Tooltip>
      </span>
      {mounted && (
        <SideDrawer title={`Repair ${serialnumber}`} onClose={() => setOpen(false)} leaving={!open}>
          <PullForm mode="repair" serialnumber={serialnumber} onPulled={load} />
          <section aria-label="Repair pulls on this drone" className="space-y-2">
            <h3 className="text-sm font-semibold text-on-surface">Repair pulls on this drone</h3>
            <Reveal show={!!loadError}>
              <Alert variant="text">{loadError}</Alert>
            </Reveal>
            <FadeIn key={lines === null ? 'loading' : lines.length === 0 && !loadError ? 'empty' : 'lines'}>
            {lines === null ? (
              <SkeletonGroup label="Loading pulls" className="space-y-1">
                <Skeleton className="h-12 w-full rounded-md" />
                <Skeleton className="h-12 w-full rounded-md" />
              </SkeletonGroup>
            ) : lines.length === 0 && !loadError ? (
              <EmptyState variant="line">
                No parts pulled for this drone yet. Scan a box above to take the first one.
              </EmptyState>
            ) : (
              <ul className="space-y-1">
                {lines.map((l) => (
                  <li
                    key={l.transaction_id}
                    className="flex items-center justify-between gap-3 rounded-md bg-surface-container px-3 py-2"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-on-surface">
                        <span className="tabular-nums">{l.quantity}</span> {itemLabel(l)}
                      </span>
                      <span className="block truncate text-xs text-on-surface-variant">
                        {formatTimestamp(l.occurred_at)}
                        {l.performed_by_name ? `, ${l.performed_by_name}` : ''}
                        {l.put_back > 0 ? `, ${l.put_back} put back` : ''}
                      </span>
                    </span>
                    <PutBackPopover line={l} onDone={load} />
                  </li>
                ))}
              </ul>
            )}
            </FadeIn>
          </section>
        </SideDrawer>
      )}
    </>
  );
};

export default DroneRepair;
